import { Clock, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { getChannelIcon } from "@/utils/channels";
import type { PublicationItem } from "./types";
import type { ChannelPublicationTime } from "@/services/business/calendar";

interface DesktopScheduleSectionProps {
	readonly allItems: PublicationItem[];
	readonly checkedDates: Record<string, boolean>;
	readonly isValidated: boolean;
	readonly isReadOnly: boolean;
	readonly onToggleDate: (key: string) => void;
	readonly onOpenSchedule: () => void;
}

export function DesktopScheduleSection({
	allItems,
	checkedDates,
	isValidated,
	isReadOnly,
	onToggleDate,
	onOpenSchedule,
}: DesktopScheduleSectionProps) {
	return (
		<div className="space-y-1.5">
			{allItems.map((item) => {
				const key = `${item.date.toISOString()}-${item.time}`;
				const isChecked = checkedDates[key] ?? true;
				const isAb = item.type === "ab";
				const YoutubeIcon = isAb ? getChannelIcon("youtube") : null;

				const isItemPublished = isAb
					? (item.data as ChannelPublicationTime)?.pubStatus === "published"
					: item.type === "regular" &&
						Array.isArray(item.data) &&
						item.data.every(
							(pub: ChannelPublicationTime) => pub.pubStatus === "published",
						);

				return (
					<div
						key={key}
						className={cn(
							"flex items-center gap-2 py-1.5 px-2 rounded-lg transition-colors",
							isItemPublished ? "opacity-40" : "hover:bg-white/[0.03]",
							isAb && !isItemPublished && "bg-violet-500/5",
						)}
					>
						{!isReadOnly && !isItemPublished && (
							<button
								onClick={() => onToggleDate(key)}
								className={cn(
									"w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all",
									isChecked
										? "border-transparent"
										: "border-white/20 bg-transparent",
								)}
								style={
									isChecked
										? {
												background: "hsl(var(--v10-status-success))",
												borderColor: "hsl(var(--v10-status-success))",
											}
										: {}
								}
							>
								<AnimatePresence>
									{isChecked && (
										<motion.div
											initial={{ scale: 0 }}
											animate={{ scale: 1 }}
											exit={{ scale: 0 }}
											transition={{
												type: "spring",
												stiffness: 500,
												damping: 30,
											}}
										>
											<Check
												className="h-2.5 w-2.5"
												style={{ color: "hsl(var(--v10-status-success))" }}
											/>
										</motion.div>
									)}
								</AnimatePresence>
							</button>
						)}

						{isItemPublished && (
							<Check
								className="h-3.5 w-3.5 flex-shrink-0"
								style={{ color: "hsl(var(--v10-text-tertiary))" }}
							/>
						)}

						{isAb ? (
							<span
								className={cn(
									"text-[10px] font-semibold flex-shrink-0",
									isItemPublished ? "text-violet-400/50" : "text-violet-400",
								)}
							>
								A/B
							</span>
						) : (
							<div className="flex items-center gap-1.5">
								<Clock
									className="h-3 w-3"
									style={{ color: "hsl(var(--v10-text-tertiary))" }}
								/>
							</div>
						)}

						{isItemPublished ? (
							<div className="flex-1 min-w-0 text-left">
								<div className="flex items-center gap-2">
									<p
										className="text-sm font-medium"
										style={{ color: "hsl(var(--v10-text-tertiary))" }}
									>
										{item.date.toLocaleDateString("fr-FR", {
											weekday: "short",
											day: "numeric",
											month: "short",
										})}
										.
									</p>
									<span
										className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
										style={{
											color: "hsl(var(--v10-status-success))",
											background: "hsl(var(--v10-status-success) / 0.1)",
										}}
									>
										Diffusé
									</span>
								</div>
								<p
									className="text-[11px]"
									style={{ color: "hsl(var(--v10-text-tertiary))" }}
								>
									{item.time}
								</p>
							</div>
						) : (
							<button
								type="button"
								className="flex-1 min-w-0 cursor-pointer text-left"
								onClick={onOpenSchedule}
							>
								<p
									className={cn(
										"text-sm font-medium",
										isAb ? "text-violet-400" : "text-white",
									)}
								>
									{item.date.toLocaleDateString("fr-FR", {
										weekday: "short",
										day: "numeric",
										month: "short",
									})}
									.
								</p>
								<p
									className="text-[11px]"
									style={{
										color: isAb
											? "rgb(167,139,250)"
											: "hsl(var(--v10-text-tertiary))",
									}}
								>
									{item.time}
								</p>
							</button>
						)}

						<div className="flex items-center gap-1">
							{isAb && YoutubeIcon ? (
								<div
									className={
										isItemPublished ? "text-violet-400/50" : "text-violet-400"
									}
								>
									<YoutubeIcon className="h-3.5 w-3.5" />
								</div>
							) : (
								item.type === "regular" &&
								Array.isArray(item.data) &&
								item.data
								.filter((pt: ChannelPublicationTime) => pt.pubStatus !== "paused")
								.map((pt: ChannelPublicationTime) => {
									const ChannelIcon = getChannelIcon(pt.channel);
									return (
										<div
											key={pt.channel}
											style={{ color: "hsl(var(--v10-text-tertiary))" }}
										>
											<ChannelIcon className="h-3.5 w-3.5" />
										</div>
									);
								})
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
